<template>
    <div class="media-preview" ref="player">
        <component :is="kind" ref="media" :src="url" preload="metadata" playsinline
            @loadedmetadata="sync" @durationchange="sync" @timeupdate="sync" @play="sync" @pause="sync"
            @ended="sync" @volumechange="sync" @error="$emit('error')" />
        <div class="media-controls">
            <b-button variant="primary" :disabled="!duration" :aria-label="$t(playing ? 'AttachmentPause' : 'AttachmentPlay')" @click="toggle">
                <feather-icon :icon="playing ? 'PauseIcon' : 'PlayIcon'" size="18" />
            </b-button>
            <span class="media-time">{{ time(position) }} / {{ time(duration) }}</span>
            <b-form-input class="media-progress" type="range" min="0" :max="duration || 1" step="0.1"
                :value="position" :disabled="!duration" :aria-label="$t('AttachmentSeek')" @input="seek" />
            <b-button variant="outline-primary" :aria-label="$t(muted ? 'AttachmentUnmute' : 'AttachmentMute')" @click="$refs.media.muted = !muted">
                <feather-icon :icon="muted || !volume ? 'VolumeXIcon' : 'Volume2Icon'" size="18" />
            </b-button>
            <b-form-input class="media-volume" type="range" min="0" max="1" step="0.05" :value="muted ? 0 : volume"
                :aria-label="$t('AttachmentVolume')" @input="setVolume" />
            <b-button v-if="kind === 'video' && fullscreenAvailable" variant="outline-primary" :aria-label="$t('AttachmentFullscreen')" @click="fullscreen">
                <feather-icon icon="MaximizeIcon" size="18" />
            </b-button>
        </div>
    </div>
</template>

<script>
import { BButton, BFormInput } from 'bootstrap-vue';

export default {
    components: { BButton, BFormInput },
    props: { url: { type: String, required: true }, kind: { type: String, required: true } },
    data: () => ({ playing: false, position: 0, duration: 0, volume: 1, muted: false, fullscreenAvailable: !!document.fullscreenEnabled }),
    beforeDestroy() { this.$refs.media.pause(); },
    methods: {
        sync() {
            const media = this.$refs.media;
            this.playing = !media.paused;
            this.position = media.currentTime;
            this.duration = Number.isFinite(media.duration) ? media.duration : 0;
            this.volume = media.volume;
            this.muted = media.muted;
        },
        async toggle() {
            try {
                if (this.playing) this.$refs.media.pause();
                else await this.$refs.media.play();
            } catch (error) { this.$emit('error'); }
        },
        seek(value) { this.$refs.media.currentTime = Number(value); },
        setVolume(value) { this.$refs.media.volume = Number(value); this.$refs.media.muted = false; },
        time(value) { return `${Math.floor(value / 60)}:${String(Math.floor(value % 60)).padStart(2, '0')}`; },
        async fullscreen() {
            try {
                if (document.fullscreenElement) await document.exitFullscreen();
                else await this.$refs.player.requestFullscreen();
            } catch (error) { /* Fullscreen can be unavailable in embedded browsers. */ }
        }
    }
};
</script>

<style scoped>
.media-preview { border: 1px solid var(--gray-200, #ebe9f1); border-radius: 6px; overflow: hidden; background: var(--white, #fff); }
video { display: block; width: 100%; max-height: 60vh; background: #000; }
.media-controls { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; padding: 12px; }
.media-progress { flex: 1; min-width: 100px; }
.media-volume { width: 80px; }
.media-time { white-space: nowrap; font-variant-numeric: tabular-nums; }
.media-preview:fullscreen { position: relative; border: 0; border-radius: 0; background: #000; }
.media-preview:fullscreen video { height: 100%; max-height: 100%; object-fit: contain; }
.media-preview:fullscreen .media-controls { position: absolute; bottom: 0; left: 0; right: 0; padding-top: 36px; color: #fff; background: linear-gradient(transparent, rgba(0, 0, 0, 0.8)); }
.media-preview:fullscreen .media-controls .btn { background: transparent !important; border-color: transparent !important; color: #fff !important; }
.media-preview:fullscreen .media-controls .btn:hover { background: rgba(255, 255, 255, 0.15) !important; }
@media (max-width: 575px) { .media-progress { order: 1; flex-basis: 100%; } }
</style>
